import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';

const RESPOND_TO_RESCHEDULE = gql`
   mutation RespondToRescheduleRequest(
      $inspectionId: ID!
      $action: RescheduleAction!
      $newSchedule: RescheduleInput
      $remarks: String
   ) {
      respondToRescheduleRequest(
         inspectionId: $inspectionId
         action: $action
         newSchedule: $newSchedule
         remarks: $remarks
      ) {
         id
         inspectionStatus
         scheduledDate
         scheduledTime
         location
         preferredSchedule {
            date
            time
         }
      }
   }
`;

const RescheduleResponseModal = ({ isOpen, onClose, inspection, onRefetch }) => {
   const [action, setAction] = useState('ACCEPT_PREFERRED');
   const [newDate, setNewDate] = useState(null);
   const [newTime, setNewTime] = useState('');
   const [remarks, setRemarks] = useState('');
   const [location, setLocation] = useState(inspection?.location || '');

   const [respondToReschedule, { loading }] = useMutation(RESPOND_TO_RESCHEDULE);

   const handleDateChange = (date) => {
      setNewDate(date);
      if (date) {
         setNewTime(format(date, 'HH:mm'));
      }
   };

   const handleTimeChange = (time) => {
      setNewTime(time);
   };

   const handleSubmit = async () => {
      try {
         if (!inspection?.id) {
            toast.error('Invalid inspection data');
            return;
         }

         const baseVariables = {
            inspectionId: inspection.id,
            action,
            remarks: remarks.trim() || undefined
         };

         let variables = { ...baseVariables };

         if (action === 'PROPOSE_NEW') {
            if (!newDate || !newTime) {
               toast.error('Please select both date and time for the new schedule');
               return;
            }

            variables.newSchedule = {
               date: format(newDate, 'yyyy-MM-dd'),
               time: newTime,
               location: location || inspection.location
            };
         }

         const { data } = await respondToReschedule({
            variables,
            refetchQueries: ['GetInspections']
         });

         if (data?.respondToRescheduleRequest) {
            const messages = {
               ACCEPT_PREFERRED: 'Preferred schedule accepted',
               PROPOSE_NEW: 'New schedule proposed',
               REJECT: 'Reschedule request rejected'
            };
            toast.success(messages[action]);
            onRefetch?.();
            onClose();
         }
      } catch (error) {
         console.error('Error submitting response:', error);
         toast.error(error.message || 'Failed to submit response');
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent>
            <DialogHeader>
               <DialogTitle>Respond to Reschedule Request</DialogTitle>
               <DialogDescription>
                  Review and respond to the reschedule request
               </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
               {action === 'ACCEPT_PREFERRED' && inspection?.preferredSchedule && (
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                     <h4 className="font-medium text-sm">Preferred Schedule Details:</h4>
                     <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                           <span className="text-muted-foreground">Date:</span>
                           <p className="font-medium">{format(new Date(inspection.preferredSchedule.date), 'PPP')}</p>
                        </div>
                        <div>
                           <span className="text-muted-foreground">Time:</span>
                           <p className="font-medium">{inspection.preferredSchedule.time}</p>
                        </div>
                        <div className="col-span-2">
                           <span className="text-muted-foreground">Location:</span>
                           <p className="font-medium">{inspection.location}</p>
                        </div>
                     </div>
                  </div>
               )}

               <div className="flex gap-2">
                  <Button
                     variant={action === 'ACCEPT_PREFERRED' ? 'default' : 'outline'}
                     onClick={() => setAction('ACCEPT_PREFERRED')}
                  >
                     Accept Preferred
                  </Button>
                  <Button
                     variant={action === 'PROPOSE_NEW' ? 'default' : 'outline'}
                     onClick={() => setAction('PROPOSE_NEW')}
                  >
                     Propose New
                  </Button>
               </div>

               {action === 'PROPOSE_NEW' && (
                  <div className="space-y-4">
                     <div className="flex gap-1">
                        <input
                           type="date"
                           value={newDate ? format(newDate, "yyyy-MM-dd") : ""}
                           onChange={(e) => {
                              const date = e.target.value ? new Date(e.target.value) : null;
                              handleDateChange(date);
                           }}
                           className="w-[150px] h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
                        />
                        <input
                           type="time"
                           value={newTime}
                           onChange={(e) => handleTimeChange(e.target.value)}
                           className="w-[150px] h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
                        />
                     </div>
                  </div>
               )}

               <Textarea
                  placeholder="Add remarks..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
               />

               <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>
                     Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={loading}>
                     Submit Response
                  </Button>
               </div>
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default RescheduleResponseModal;
